<script setup lang="ts">
import type { ChampionshipRegistrationAdditionalCost } from "@/model/championship/championship-registration-additional-cost"
import type { ChampionshipRegistrationTeam } from "@/model/championship/championship-registration-team"
import {
    type RegistrationPermissions,
    SelectTeamDataInjection,
} from "@/pages/Tasks/Tasks/championship-registration"
import { useRegistrationPolicy } from "@/pages/Tasks/Tasks/useRegistrationPolicy"
import { defineTranslations } from "@/utils/defineTranslations"
import { $currency, injectMandatory } from "@/vf"
import { computed, inject, ref } from "vue"
import { TaskViewMode } from "../../Issue/util"
import ChampionshipRegistrationEditAdditionalCostsAdmin from "./ChampionshipRegistrationEditAdditionalCostsAdmin.vue"

const props = defineProps<{
    registrationId: string
    team: ChampionshipRegistrationTeam
    additionalCosts: ChampionshipRegistrationAdditionalCost[]
    permissions?: RegistrationPermissions
}>()

const emit = defineEmits<{
    (e: "updated"): void
}>()

const viewMode = injectMandatory(TaskViewMode, "Missing mandatory injection: TaskViewMode")
const injectedData = inject(SelectTeamDataInjection, null)

if (!props.permissions && !injectedData) {
    throw new Error("ChampionshipRegistrationAdditionalCostsSectionAdmin requires permissions prop or SelectTeamDataInjection")
}

const permissions = computed(() => props.permissions ?? injectedData?.value?.permissions)
const policy = useRegistrationPolicy(permissions, computed(() => viewMode))

const opened = ref(false)
const total = computed(() => props.additionalCosts.reduce((sum, c) => sum + (c.price ?? 0) * (c.quantity ?? 1), 0))

function toggle() {
    if (!policy.value.ViewAdditionalCosts) {
        return
    }

    opened.value = !opened.value
}

function onUpdated() {
    opened.value = false
    emit("updated")
}

const trans = defineTranslations({
    edit_additional_costs: {
        de: "Zusätzliche Kosten verwalten",
        en: "Manage additional costs",
    },
    additional_costs_description: {
        de: "{count} zusätzliche Kosten, Gesamtsumme: {total}",
        en: "{count} additional costs, total: {total}",
    },
    additional_costs_none: {
        de: "Keine zusätzlichen Kosten",
        en: "No additional costs",
    },
})
</script>

<template>
    <template v-if="policy.ViewAdditionalCosts">
        <div class="d-flex align-items-center py-3 border-top border-gray uploadable" @click="toggle">
            <div class="mx-3">
                <i class="fas fa-fw fa-euro-sign text-primary"></i>
            </div>
            <div>
                <div class="font-weight-bold">
                    {{ trans("edit_additional_costs") }}
                </div>
                <div class="text-muted">
                    <template v-if="additionalCosts.length > 0">
                        {{
                            trans("additional_costs_description", {
                                count: additionalCosts.length,
                                total: $currency(total),
                            })
                        }}
                    </template>
                    <template v-else>
                        {{ trans("additional_costs_none") }}
                    </template>
                </div>
            </div>
        </div>

        <div v-if="opened">
            <ChampionshipRegistrationEditAdditionalCostsAdmin
                :registration-id="registrationId"
                :team="team"
                :additional-costs="additionalCosts"
                :permissions="permissions"
                @updated="onUpdated"
            />
        </div>
    </template>
</template>

<style scoped>
.uploadable {
    cursor: pointer;
}

.uploadable:hover {
    background-color: var(--light);
}
</style>
